import React, {useState, useMemo} from "react";
import {Box, Stack, Typography, Button} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import ModalWithContent from "../../modal/ModalWithContent";
import {TextInput, SelectInput} from "../../../components/ui/inputs";
import {PrimaryButton, DeleteButton, SecondaryButton} from "../../../components/ui/buttons";
import {FormSection} from "../../../components/ui/blocks";
import {Badge} from "../../../components/ui/badges";

export default function DozorTeamWithPointSequence({accessType, sequenceList, team, pointSet, transitionSet, dozorState}) {
    const [selectionSequence, setSelectionSequence] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSequenceOpen, setModalSequenceOpen] = useState(false);

    const sortedSequences = useMemo(() =>
        [...(sequenceList || [])].sort((a, b) => a.orderId - b.orderId),
        [sequenceList]
    );

    const handleAddSequence = async (entityType, entityId) => {
        try {
            await dozorState.addSequence(team.id, entityType, entityId);
            setModalOpen(false);
        } catch (error) {
            // Ошибка уже обработана в хуке
        }
    };

    const handleDeleteSequence = async (sequenceId) => {
        try {
            await dozorState.deleteSequence(sequenceId);
            setModalSequenceOpen(false);
        } catch (error) {
            // Ошибка уже обработана в хуке
        }
    };

    const handleSaveSequence = async (sequenceId, updatedData) => {
        try {
            await dozorState.saveSequence(sequenceId, updatedData);
            setModalSequenceOpen(false);
        } catch (error) {
            // Ошибка уже обработана в хуке
        }
    };

    const handleSequenceClick = (sequence) => {
        if (accessType === "EDIT") {
            setSelectionSequence(sequence);
            setModalSequenceOpen(true);
        }
    };

    const handleExcludeTeam = async () => {
        if (!window.confirm(`Удалить команду "${team.teamName}" из игры?`)) return;
        await dozorState.deleteTeam(team.id);
    };

    const headerAction = accessType === "EDIT" ? (
        <Stack direction="row" spacing={1} alignItems="center">
            <Button
                size="small"
                color="error"
                startIcon={<PersonRemoveIcon />}
                onClick={handleExcludeTeam}
                sx={{textTransform: 'none'}}
            >
                Удалить
            </Button>
            <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setModalOpen(true)}
                sx={{textTransform: 'none'}}
            >
                Добавить
            </Button>
        </Stack>
    ) : accessType === "MANAGE" ? (
        <Badge label={`${team.balance} баллов`} color="primary" variant="filled" />
    ) : null;

    return (
        <FormSection title={team.teamName} action={headerAction}>
            {sortedSequences.length > 0 ? (
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    {sortedSequences.map((sequence) => (
                        <SequenceBadge
                            key={sequence.id}
                            sequence={sequence}
                            accessType={accessType}
                            pointSet={pointSet}
                            transitionSet={transitionSet}
                            onClick={() => handleSequenceClick(sequence)}
                        />
                    ))}
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                    Последовательность не задана
                </Typography>
            )}

            <ModalWithContent
                content={
                    <AddSequenceModal
                        pointSet={pointSet}
                        transitionSet={transitionSet}
                        onAdd={handleAddSequence}
                        onClose={() => setModalOpen(false)}
                    />
                }
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="xs"
            />

            <ModalWithContent
                content={
                    selectionSequence && (
                        <EditSequenceModal
                            sequence={selectionSequence}
                            pointSet={pointSet}
                            transitionSet={transitionSet}
                            onDelete={handleDeleteSequence}
                            onSave={handleSaveSequence}
                            onClose={() => setModalSequenceOpen(false)}
                        />
                    )
                }
                isOpen={modalSequenceOpen}
                onClose={() => setModalSequenceOpen(false)}
                maxWidth="xs"
            />
        </FormSection>
    );
}

function SequenceBadge({sequence, accessType, pointSet, transitionSet, onClick}) {
    const isPoint = sequence.dozorGameEntityType === 'POINT';
    const entity = isPoint
        ? (sequence.dozorPoint || pointSet.find(p => p.id === sequence.entityId))
        : (sequence.dozorTransition || transitionSet.find(t => t.id === sequence.entityId));
    const title = entity?.title || 'Не выбрано';

    const isWaiting = accessType === "MANAGE" && sequence.pointProcessingStatus === 'WAITING';
    const color = isWaiting ? 'default' : (isPoint ? 'success' : 'info');

    return (
        <Badge
            label={`${sequence.orderId}. ${title}`}
            color={color}
            variant="filled"
            onClick={accessType === "EDIT" ? onClick : undefined}
            sx={accessType === "EDIT" ? {cursor: 'pointer'} : undefined}
        />
    );
}

function AddSequenceModal({pointSet, transitionSet, onAdd, onClose}) {
    const [entityType, setEntityType] = useState('POINT');
    const [entityId, setEntityId] = useState('');

    const isPoint = entityType === 'POINT';
    const options = isPoint
        ? pointSet.map(p => ({key: p.id, value: p.title || `Точка ${p.orderId}`}))
        : transitionSet.map(t => ({key: t.id, value: t.title || `Переход ${t.orderId}`}));

    const handleAdd = () => {
        if (!entityId) return;
        onAdd(entityType, entityId);
    };

    return (
        <Box sx={{width: {xs: '100%', sm: 400}}}>
            <Typography variant="h4" color="text.primary" sx={{mb: 3, textAlign: 'center'}}>
                Добавить в последовательность
            </Typography>

            <Stack spacing={2.5}>
                <SelectInput
                    label="Тип"
                    options={[
                        {key: 'POINT', value: 'Точка'},
                        {key: 'TRANSITION', value: 'Переход'}
                    ]}
                    value={entityType}
                    onChange={(val) => {
                        setEntityType(val);
                        setEntityId('');
                    }}
                />

                <SelectInput
                    label={isPoint ? 'Точка' : 'Переход'}
                    options={options}
                    value={entityId}
                    onChange={setEntityId}
                />

                <Stack direction="row" spacing={2} sx={{pt: 1}}>
                    <SecondaryButton onClick={onClose}>
                        Отмена
                    </SecondaryButton>
                    <PrimaryButton onClick={handleAdd} disabled={!entityId}>
                        Добавить
                    </PrimaryButton>
                </Stack>
            </Stack>
        </Box>
    );
}

function EditSequenceModal({sequence, pointSet, transitionSet, onDelete, onSave, onClose}) {
    const [sequenceData, setSequenceData] = useState({
        dozorGameEntityType: sequence.dozorGameEntityType,
        dozorPoint: sequence.dozorPoint,
        dozorTransition: sequence.dozorTransition,
        orderId: sequence.orderId
    });

    const isPoint = sequenceData.dozorGameEntityType === 'POINT';
    const entityLabel = isPoint ? 'Точка' : 'Переход';
    const currentEntity = isPoint ? sequenceData.dozorPoint : sequenceData.dozorTransition;

    const options = isPoint
        ? pointSet.map(p => ({key: p.id, value: p.title || `Точка ${p.orderId}`}))
        : transitionSet.map(t => ({key: t.id, value: t.title || `Переход ${t.orderId}`}));

    const handleFieldChange = (field, value) => {
        setSequenceData(prev => ({...prev, [field]: value}));
    };

    const handleEntityChange = (entityId) => {
        if (isPoint) {
            handleFieldChange("dozorPoint", pointSet.find(p => p.id === entityId));
        } else {
            handleFieldChange("dozorTransition", transitionSet.find(t => t.id === entityId));
        }
    };

    const handleSave = () => {
        const updatedSequence = {
            ...sequence,
            dozorGameEntityType: sequenceData.dozorGameEntityType,
            dozorPoint: sequenceData.dozorPoint,
            dozorTransition: sequenceData.dozorTransition,
            orderId: sequenceData.orderId
        };
        onSave(sequence.id, updatedSequence);
    };

    return (
        <Box sx={{width: {xs: '100%', sm: 400}}}>
            <Typography variant="h4" color="text.primary" sx={{mb: 3, textAlign: 'center'}}>
                Редактирование
            </Typography>

            <Stack spacing={2.5}>
                <TextInput
                    label="Порядковый номер"
                    value={sequenceData.orderId}
                    onChange={e => handleFieldChange('orderId', e.target.value)}
                />

                <SelectInput
                    label={entityLabel}
                    options={options}
                    value={currentEntity?.id || ''}
                    onChange={handleEntityChange}
                />

                <Stack direction="row" spacing={2} sx={{pt: 1}}>
                    <DeleteButton onClick={() => onDelete(sequence.id)}>
                        Удалить
                    </DeleteButton>
                    <PrimaryButton onClick={handleSave}>
                        Сохранить
                    </PrimaryButton>
                </Stack>
            </Stack>
        </Box>
    );
}
